import { useProfileContext } from "@hooks/context/useProfileContext";
import { useSupportLauncherContext } from "@hooks/context/useSupportLauncherContext";
import { useIntentKitAgentChat } from "@layout/GlobalAssistant/hooks/useIntentKitAgentChat";
import { Stack, useTheme } from "@mui/material";
import { BaseDrawerShell } from "@ui/organisms/BaseDrawerShell/BaseDrawerShell";
import { AIAssistantChatShell } from "@layout/GlobalAssistant/WorkflowChat/AIAssistantChatShell";
import { showToast } from "@utils/toast";
import BaseTypography from "@ui/atoms/BaseTypography/BaseTypography";
import Logo from "@ui/atoms/Logo/Logo";

const notifyUserInvited = () => showToast("User invited successfully!");

// The Navigation-lane assistant chat drawer.
//
// The launcher that opens this drawer now lives in the sidebar profile section
// (MainSidebarProfileSection), folded into the same SpeedDial as Settings /
// Admin / Logout / Contact support. This component owns only the drawer; its
// open-state and the assistant-enabled gate come from SupportLauncherContext so
// the trigger and the drawer stay in sync without prop-drilling.
//
// Rendering happens once in RootShell so the in-flight agent stream survives
// layout swaps triggered by agent-initiated navigation.
export const GlobalAssistant = (): JSX.Element | null => {
  const { userOrganisation, userProfile } = useProfileContext();
  const { assistantEnabled, assistantOpen, setAssistantOpen } =
    useSupportLauncherContext();

  // Only mount the chat when the assistant is actually available. `canChat`
  // (now `assistantEnabled` in context) already requires a loaded org + profile.
  if (!assistantEnabled || !userOrganisation || !userProfile) return null;

  return (
    <GlobalAssistantInner
      firstName={userProfile.firstName}
      open={assistantOpen}
      orgId={userOrganisation.id}
      setOpen={setAssistantOpen}
      userId={userProfile.id}
    />
  );
};

export type InnerProps = {
  firstName?: string;
  open: boolean;
  orgId: string;
  setOpen: (open: boolean) => void;
  userId: string;
};

export const GlobalAssistantInner = ({
  firstName,
  open,
  orgId,
  setOpen,
  userId
}: InnerProps): JSX.Element => {
  const theme = useTheme();
  const chat = useIntentKitAgentChat({
    onUserCreated: notifyUserInvited,
    orgId,
    userId
  });

  return (
    <BaseDrawerShell
      open={open}
      onClose={() => setOpen(false)}
      title={
        <Logo
          variant="mark"
          width={46}
          startColor={theme.vars.ui.logo.gradient.from}
          endColor={theme.vars.ui.logo.gradient.to}
        />
      }
      drawerWidth={460}
      customContentLayout
    >
      <Stack
        sx={{
          flex: 1,
          justifyContent: "flex-end",
          minHeight: 0,
          width: "100%"
        }}
      >
        <AIAssistantChatShell
          key={chat.sessionKey}
          composerDisabled={chat.composerDisabled}
          contextUsage={chat.contextUsage}
          firstName={firstName}
          isAwaitingIntent={chat.isAwaitingIntent}
          isProcessing={chat.isProcessing}
          messages={chat.messages}
          pendingConfirmation={chat.pendingConfirmation}
          onCancelAction={chat.cancelAction}
          onClearChat={chat.reset}
          onConfirmAction={chat.confirmAction}
          sendUserMessage={chat.sendUserMessage}
          statusLine={chat.statusLine}
          stopGenerating={chat.stopGenerating}
        />

        <BaseTypography
          variant="menuCaption"
          text="AI-generated content may be inaccurate."
          sx={{
            color: theme.vars.palette.text.quaternary,
            mb: -1,
            mt: 2,
            textAlign: "center",
            width: "100%"
          }}
        />
      </Stack>
    </BaseDrawerShell>
  );
};
